import React, { useState, useEffect } from 'react';
import { getVersionInfo, getEnvironmentBadgeColor, getEnvironmentBadgeText } from '../services/versionService';
import styles from '../styles/aboutModal.module.css';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ReleaseNote {
  version: string;
  date: string;
  notes: string[];
}

const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
  const [versionInfo, setVersionInfo] = useState<any>(null);
  const [badgeColor, setBadgeColor] = useState<string>('');
  const [badgeText, setBadgeText] = useState<string>('');
  const [aboutContent, setAboutContent] = useState<string>('');
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);

  // Load version information
  useEffect(() => {
    if (isOpen) {
      const loadVersionInfo = async () => {
        try {
          const info = await getVersionInfo();
          setVersionInfo(info);
          setBadgeColor(getEnvironmentBadgeColor());
          setBadgeText(getEnvironmentBadgeText());
        } catch (error) {
          console.error('Error loading version info:', error);
          // Fallback to basic info
          setVersionInfo({
            version: '1.0.0',
            environment: 'Local Development',
            lastDeployed: new Date().toLocaleString()
          });
          setBadgeColor(getEnvironmentBadgeColor());
          setBadgeText(getEnvironmentBadgeText());
        }
      };
      
      loadVersionInfo();
    }
  }, [isOpen]);

  // Fetch the about content directly
  useEffect(() => {
    if (isOpen) {
      fetch('/content/Recovery-Commons-About.md')
        .then(response => response.text())
        .then(content => setAboutContent(content))
        .catch(error => {
          console.error('Error loading about content:', error);
          setAboutContent('# About Recovery Commons\n\nUnable to load content at this time.');
        });
    }
  }, [isOpen]);

  // Fetch release notes
  useEffect(() => {
    if (isOpen) {
      fetch('/content/release-notes.json')
        .then(response => response.ok ? response.json() : [])
        .then((data: ReleaseNote[]) => setReleaseNotes(Array.isArray(data) ? data : []))
        .catch(() => setReleaseNotes([]));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>About Recovery Commons</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* About Content - Direct Markdown Display */}
          <div style={{
            lineHeight: '1.6',
            color: '#2c3e50',
            marginBottom: '20px'
          }}>
            {aboutContent ? (
              <div dangerouslySetInnerHTML={{ 
                __html: aboutContent
                  .replace(/^# (.*$)/gim, '') // Remove the first # heading to avoid duplicate title
                  .replace(/^## (.*$)/gim, '<h2 style="color: #2c3e50; margin: 20px 0 15px 0; font-size: 1.3rem; font-weight: 600;">$1</h2>')
                  .replace(/^### (.*$)/gim, '<h3 style="color: #2c3e50; margin: 15px 0 10px 0; font-size: 1.1rem; font-weight: 600;">$1</h3>')
                  .replace(/^#### (.*$)/gim, '<h4 style="color: #2c3e50; margin: 15px 0 10px 0; font-size: 1rem; font-weight: 600;">$1</h4>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\*(.*?)\*/g, '<em>$1</em>')
                  .replace(/`(.*?)`/g, '<code style="background: #f8f9fa; padding: 2px 4px; border-radius: 3px; font-family: monospace;">$1</code>')
                  .replace(/\n\n/g, '</p><p>')
                  .replace(/^/, '<p>')
                  .replace(/$/, '</p>')
                  .replace(/^<p><\/p>/, '') // Remove empty paragraph if first heading was removed
              }} />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                Loading about content...
              </div>
            )}
          </div>

          {/* Release Notes / What's New */}
          {releaseNotes.length > 0 && (
            <div style={{
              borderTop: '1px solid #e1e5e9',
              paddingTop: '20px',
              marginTop: '20px'
            }}>
              <h3 style={{
                color: '#2c3e50',
                fontSize: '1.1rem',
                fontWeight: 600,
                marginBottom: '16px'
              }}>
                What&apos;s New
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {releaseNotes.map((note, index) => (
                  <div key={index} style={{
                    padding: '12px 0',
                    borderBottom: index < releaseNotes.length - 1 ? '1px solid #eee' : 'none'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '12px',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontSize: '1rem'
                      }}>
                        v{note.version}
                      </span>
                      <span style={{
                        color: '#6c757d',
                        fontSize: '0.9rem'
                      }}>
                        {new Date(note.date + 'T12:00:00').toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <ul style={{
                      margin: 0,
                      paddingLeft: '20px',
                      color: '#495057',
                      lineHeight: 1.6,
                      fontSize: '0.95rem'
                    }}>
                      {note.notes.map((item, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Compact Version Information at Bottom */}
          <div style={{
            borderTop: '1px solid #e1e5e9',
            paddingTop: '16px',
            marginTop: '20px',
            fontSize: '0.85rem',
            color: '#6c757d'
          }}>
            {versionInfo ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                {/* Environment Badge */}
                <div style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  backgroundColor: badgeColor
                }}>
                  {badgeText}
                </div>
                
                {/* Version Info */}
                <span><strong>v{versionInfo.version}</strong></span>
                <span>•</span>
                <span>{versionInfo.environment}</span>
                <span>•</span>
                <span>Deployed: {versionInfo.lastDeployed}</span>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                Loading version information...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutModal; 